/**
 * Puente con la APK. La cola de lecturas vive en Android, no en localStorage.
 *
 * El plan de cada parada (tag o GPS) puede traer consignas:
 * { id_checks, descripcion }.
 *
 * Cada elemento de obtenerColaLocal() debe traer:
 * planMarcaId, horaReporte (ISO de la lectura real), novedad,
 * consignas: [{ id_checks, valor }] con valor 1 = SI y 0 = NO,
 * fotoBase64 opcional (JPEG, sin prefijo data:image). Si no hay foto, se omite.
 * tagUid si es tag, o lat, lng y precision si es GPS.
 * clientUuid debe repetirse igual si esa lectura se reenvía.
 */

export const puenteAndroid = () => {
  if (typeof window === 'undefined') return null;
  const bridge = window.AndroidInterface;
  if (!bridge) return null;
  return bridge;
};

const parseJson = (raw) => {
  if (raw == null || raw === '') return null;
  if (typeof raw === 'object') return raw;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
};

export const leerPlanAndroid = () => {
  const bridge = puenteAndroid();
  if (!bridge || typeof bridge.obtenerPlanLocal !== 'function') return null;
  const data = parseJson(bridge.obtenerPlanLocal());
  if (!data) return null;
  const cuerpo = data.data && typeof data.data === 'object' ? data.data : data;
  const marcas = cuerpo.marcas || cuerpo.paradas || cuerpo.puntos || [];
  if (!Array.isArray(marcas) || !marcas.length) return null;
  return {
    ...cuerpo,
    marcas: marcas.map((marca, index) => ({
      id: marca.id ?? marca.planMarcaId ?? marca.plan_marca_id,
      numero: marca.numero ?? marca.orden ?? index + 1,
      nombre: marca.nombre || marca.nombreMarca || `Punto ${index + 1}`,
      tipo: String(marca.tipo || marca.tipo_parada || '').toUpperCase() === 'GPS' ? 'GPS' : 'TAG',
      latitud: marca.latitud ?? marca.lat ?? null,
      longitud: marca.longitud ?? marca.lng ?? null,
      radio_m: marca.radio_m ?? marca.radio ?? null,
      tag_uid: marca.tag_uid || marca.tagNfc || marca.tag || null,
      consignas: Array.isArray(marca.consignas)
        ? marca.consignas.map((item) => ({
            id_checks: item.id_checks ?? item.id_check,
            descripcion: item.descripcion || ''
          }))
        : []
    })),
    marcasEsperadas: cuerpo.marcasEsperadas || marcas.length
  };
};

const uuidDeLectura = (planMarcaId, horaReporte, clientUuid) => {
  const propio = String(clientUuid || '').trim();
  if (propio) return propio.slice(0, 64);
  const base = `ronda-${planMarcaId}-${horaReporte || 'sin-hora'}`;
  return base.slice(0, 64);
};

export const leerColaAndroid = () => {
  const bridge = puenteAndroid();
  if (!bridge || typeof bridge.obtenerColaLocal !== 'function') return [];
  const data = parseJson(bridge.obtenerColaLocal());
  const lista = Array.isArray(data) ? data : data?.registros || data?.cola || data?.items || [];
  if (!Array.isArray(lista)) return [];
  return lista
    .map((item) => {
      const planMarcaId = item.planMarcaId ?? item.plan_marca_id ?? item.marcaId ?? item.idMarca;
      const horaReporte = item.horaReporte || item.hora_reporte || item.timestamp || item.fechaHora || item.fecha_hora || null;
      if (planMarcaId == null) return null;
      const consignasRaw = item.consignas || item.checks || item.respuestas || [];
      return {
        planMarcaId,
        clientUuid: uuidDeLectura(planMarcaId, horaReporte, item.clientUuid || item.client_uuid || item.uuid),
        horaReporte,
        lat: item.lat ?? item.latitud ?? null,
        lng: item.lng ?? item.longitud ?? item.lon ?? null,
        precision: item.precision ?? item.precision_m ?? item.accuracy ?? item.gps_precision_m ?? null,
        tagUid: item.tagUid || item.tag_uid || item.tag || item.uid || null,
        novedad: String(item.novedad || item.Reportar_Novedad_Cliente || '').trim(),
        ...(String(item.fotoBase64 || item.foto_base64 || '').trim()
          ? { fotoBase64: String(item.fotoBase64 || item.foto_base64).trim() }
          : {}),
        consignas: Array.isArray(consignasRaw)
          ? consignasRaw.map((respuesta) => ({
              id_checks: respuesta.id_checks ?? respuesta.id_check ?? respuesta.idChecks,
              valor: respuesta.valor
            }))
          : []
      };
    })
    .filter(Boolean);
};

export const abrirPantallaRondaLocal = () => {
  const bridge = puenteAndroid();
  if (!bridge || typeof bridge.abrirVistaOffline !== 'function') return false;
  bridge.abrirVistaOffline();
  return true;
};

export const descargarPlanAndroid = (idGenerado, username) => {
  const bridge = puenteAndroid();
  if (!bridge || typeof bridge.descargarPlanRonda !== 'function') return false;
  const puesto = String(idGenerado || '').trim();
  const usuario = String(username || '').trim();
  if (!puesto || !usuario) return false;
  bridge.descargarPlanRonda(puesto, usuario);
  return true;
};

export const leerTagAndroid = (planMarcaId, nombre) => {
  const bridge = puenteAndroid();
  if (!bridge || typeof bridge.prepararEscaneoNfc !== 'function') return false;
  bridge.prepararEscaneoNfc(Number(planMarcaId), String(nombre || ''));
  return true;
};

export const leerGpsAndroid = (planMarcaId, nombre) => {
  const bridge = puenteAndroid();
  if (!bridge || typeof bridge.reportarGps !== 'function') return false;
  bridge.reportarGps(Number(planMarcaId), String(nombre || ''));
  return true;
};

export const limpiarColaAndroid = () => {
  const bridge = puenteAndroid();
  if (!bridge || typeof bridge.limpiarColaLocal !== 'function') return false;
  bridge.limpiarColaLocal();
  return true;
};
